import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Trans, useLingui } from '@lingui/react/macro';
import { acceptInvitation } from '../../api/invitations';
import { useFeature, NotFoundPage } from '../../feature-gate';

type Status = 'loading' | 'success' | 'already_member' | 'error';

/** Why the accept failed, as a code: translated at render, never the server's English text. */
type FailReason = 'invalid_link' | 'failed';

/**
 * The accept handler (internal/api/admin/handler.go) answers a malformed token
 * with 400, which is this page's own "invalid link" case. Every other refusal
 * (404 not found, 410 expired, 409 already accepted or revoked, 401, a 500)
 * shows the page's existing generic line: no NEW descriptive sentence on an
 * error surface while D-318's reach beyond chat is unruled (PM, 2026-09-29).
 */
function reasonFor(status: number | undefined): FailReason {
  return status === 400 ? 'invalid_link' : 'failed';
}

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  // Rides the existing invite feature key: the accept endpoint
  // (POST /api/v1/invitations/{id}/accept) has a handler that was never mounted
  // on the gateway, so the page cannot work until the invite feature ships
  // (docs/research/spread-scan-20260723-120016-fe-dead-routes).
  const inviteEnabled = useFeature('tenant.members.invite');
  const { t } = useLingui();
  const [status, setStatus] = useState<Status>('loading');
  const [failReason, setFailReason] = useState<FailReason>('failed');

  useEffect(() => {
    if (!inviteEnabled) return;
    if (!token) {
      setStatus('error');
      setFailReason('invalid_link');
      return;
    }

    acceptInvitation(token)
      .then((result) => {
        setStatus(result.status === 'already_member' ? 'already_member' : 'success');
      })
      .catch((err) => {
        setStatus('error');
        // The server's (English) message stays in the log only (EN sweep
        // 2026-09-29, B-auth-invite); a 500 would even carry raw DB text.
        console.warn('[InviteAcceptPage] accept refused:', err?.response?.status, err?.response?.data?.message ?? err?.message);
        setFailReason(reasonFor(err?.response?.status));
      });
  }, [token, inviteEnabled]);

  if (!inviteEnabled) {
    return <NotFoundPage homePath="/" />;
  }

  const failMessage: Record<FailReason, string> = {
    invalid_link: t`Invalid invitation link.`,
    failed: t`Failed to accept invitation.`,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 4,
        textAlign: 'center',
      }}
    >
      {status === 'loading' && (
        <>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">
            <Trans>Accepting invitation...</Trans>
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            <Trans>Invitation accepted!</Trans>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            <Trans>You have been added to the workspace.</Trans>
          </Typography>
          <Button variant="contained" onClick={() => navigate('/agents')}>
            <Trans>Go to workspace</Trans>
          </Button>
        </>
      )}

      {status === 'already_member' && (
        <>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: 'info.main', mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            <Trans>You are already a member</Trans>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            <Trans>You already have access to this workspace.</Trans>
          </Typography>
          <Button variant="contained" onClick={() => navigate('/agents')}>
            <Trans>Go to workspace</Trans>
          </Button>
        </>
      )}

      {status === 'error' && (
        <>
          <ErrorOutlineIcon
            sx={{ fontSize: 64, color: 'error.main', mb: 2 }}
          />
          <Typography variant="h5" color="error" gutterBottom>
            <Trans>Invitation failed</Trans>
          </Typography>
          <Alert severity="error" sx={{ mb: 3, maxWidth: 480 }}>
            {failMessage[failReason]}
          </Alert>
          <Button variant="outlined" onClick={() => navigate('/agents')}>
            <Trans>Go to workspace</Trans>
          </Button>
        </>
      )}
    </Box>
  );
}
